import { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';

const FilterIcon = ({ active }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={active ? "#7CC805" : "currentColor"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const CheckIcon = () => (
  <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
    <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * ColumnFilterDropdown
 *
 * Renders a filter icon button inside a table header cell.
 * Clicking it opens a portal dropdown with search, checkboxes, apply/reset.
 *
 * Props:
 *   label     – column display name (used for tooltip)
 *   options   – string[] of available filter values
 *   selected  – string[] of currently selected values
 *   onApply   – (values: string[]) => void — called on apply or reset
 */
export default function ColumnFilterDropdown({ label, options, selected, onApply }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState(selected);
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: null, right: null, width: 0, maxHeight: 320 });

  useEffect(() => { setDraft(selected); }, [selected]);

  useEffect(() => {
    if (open && btnRef.current) {
      const th = btnRef.current.closest('th') || btnRef.current;
      const rect = th.getBoundingClientRect();
      const dropdownWidth = 280;
      const wouldOverflow = rect.left + dropdownWidth > window.innerWidth;
      const spaceBelow = window.innerHeight - rect.bottom - 20;
      setPos({
        top: rect.bottom,
        left: wouldOverflow ? null : rect.left,
        right: wouldOverflow ? window.innerWidth - rect.right : null,
        width: rect.width,
        maxHeight: Math.max(spaceBelow, 180),
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          btnRef.current && !btnRef.current.contains(e.target)) {
        setOpen(false);
        setDraft(selected);
        setQuery("");
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, selected]);

  const filtered = useMemo(() =>
    options.filter(o => o?.toString().toLowerCase().includes(query.toLowerCase())),
    [options, query]
  );

  const toggle = (val) => {
    setDraft(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  };

  const handleApply = () => {
    onApply(draft);
    setOpen(false);
    setQuery("");
  };

  const handleReset = () => {
    setDraft([]);
    onApply([]);
    setOpen(false);
    setQuery("");
  };

  const isActive = selected.length > 0;

  return (
    <>
      <button
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        style={{
          background: "none", border: "none", cursor: "pointer", padding: "2px 4px",
          color: isActive ? "#7CC805" : "#898989", display: "flex",
        }}
        title={`Фильтр: ${label}`}
      >
        <FilterIcon active={isActive} />
        {isActive && (
          <span style={{ fontSize: 12, lineHeight: "100%", fontWeight: 700, color: "#7CC805", marginLeft: 2, marginBottom: "auto" }}>{selected.length}</span>
        )}
      </button>

      {open && ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed", top: pos.top, left: pos.left, right: pos.right, zIndex: 99999,
              background: "#fff", border: "1px solid #E7E7E7", borderRadius: 12,
              boxShadow: "0 8px 32px #00000014", minWidth: Math.max(pos.width, 220), maxWidth: 280,
              maxHeight: pos.maxHeight, overflow: "hidden",
              padding: "10px 0 0 0", display: "flex", flexDirection: "column",
            }}
          >
            <div style={{ padding: "0 10px 8px" }}>
              <input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Поиск…"
                style={{
                  width: "100%", boxSizing: "border-box", padding: "7px 10px",
                  borderRadius: 8, border: "1.5px solid #e7e7e7", fontSize: 13,
                  outline: "none", fontFamily: "inherit",
                }}
              />
            </div>

            <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {filtered.length === 0
                ? <div style={{ padding: "8px 14px", fontSize: 13, color: "#aaa" }}>Нет результатов</div>
                : filtered.map((opt) => {
                    const checked = draft.includes(opt);
                    return (
                      <div
                        key={opt}
                        onClick={() => toggle(opt)}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "8px 14px", cursor: "pointer", fontSize: 13,
                          background: checked ? "#f9ffe6" : "#fff",
                          borderBottom: "1px solid #f5f5f5",
                        }}
                      >
                        <span style={{
                          width: 16, height: 16, minWidth: 16, borderRadius: 4, flexShrink: 0,
                          border: `2px solid ${checked ? "#7CC805" : "#c5c5c5"}`,
                          background: checked ? "#7CC805" : "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          {checked && <CheckIcon />}
                        </span>
                        <span style={{ color: "#333" }}>{opt}</span>
                      </div>
                    );
                  })
              }
            </div>

            <div style={{ display: "flex", gap: 8, padding: "10px", borderTop: "1px solid #f0f0f0" }}>
              <button
                onClick={handleReset}
                style={{
                  flex: 1, padding: "7px", borderRadius: 8, border: "1.5px solid #e7e7e7",
                  background: "#fff", color: "#555", fontSize: 12.5, fontWeight: 500, cursor: "pointer",
                }}
              >Сбросить</button>
              <button
                onClick={handleApply}
                style={{
                  flex: 1, padding: "7px", borderRadius: 8, border: "none",
                  background: "#7CC805", color: "#fff", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                }}
              >Применить</button>
            </div>
        </div>,
        document.body
      )}
    </>
  );
}
